import { z } from 'zod';
import { loadJson, removeKey, saveJson } from '@/lib/storage';
import { ORDER_TYPE_WIRE_VALUES } from '@/types/checkout';

/**
 * Draft ticket persistence (V1 restaurant fast-follow) — refresh/reconnect/
 * crash recovery for the in-progress ticket.
 *
 * The register cart is in-memory only, so a phone tab killed under memory
 * pressure, a pull-to-refresh, or a PWA service-worker update throws away a
 * ticket a waiter has been building at the table. This module persists just
 * enough to REBUILD that ticket and nothing else.
 *
 * WHAT IS PERSISTED, AND WHY ONLY THIS (01-architecture.md §7 / storage.ts):
 * the bearer token already sits in localStorage as an accepted XSS exposure;
 * the standing rule is not to widen that exposure. So the draft carries only
 * catalog references and counts — product ids, quantities, the variant/option
 * attribute map, per-line addon ids + quantities, the order type, and the
 * scope key the draft belongs to.
 *
 * DELIBERATELY NOT PERSISTED:
 * - customer_name / phone / email and any address — customer PII. An
 *   abandoned draft on a shared counter device must not hand the next person
 *   a customer's phone number, and XSS must not be able to harvest a day of
 *   diner contact details. Re-keying a name and phone is a few seconds; the
 *   items are the part that is expensive to rebuild.
 * - free-text notes (order notes and per-line item notes). In practice these
 *   carry delivery landmarks, table/customer identifiers and other unbounded
 *   personal detail, so they fall under the same rule. `sanitizeAttributes`
 *   also strips an `attributes.notes` key defensively, because that key is
 *   the wire path item notes ride on (types/checkout.ts).
 * - money fields (discount, paid amount) and the selected delivery area:
 *   restoring an amount the cashier never re-authorised is worse than
 *   re-entering it, and the server reprices everything anyway.
 * - product names/prices. Restore resolves ids against the LIVE catalog, so a
 *   ticket can never come back priced from a stale snapshot.
 */

/** Versioned like every other persisted key ('sg.auth.v1', 'sg.theme'). */
export const DRAFT_STORAGE_KEY = 'sg.cart-draft.v1';

/**
 * Staleness cutoff — 12 hours.
 *
 * Recovery is for "this shift", not "this week": a ticket from yesterday's
 * lunch is noise a cashier has to notice and clear, and silently reviving it
 * risks charging a table for someone else's order. 12 hours covers the
 * longest realistic single service (a lunch-through-late-dinner day) and
 * survives a device sleeping overnight-into-open only if it really was within
 * half a day. A calendar-day boundary was rejected on purpose: restaurants
 * trade past midnight, and a draft taken at 23:55 must still recover at
 * 00:05.
 */
export const DRAFT_MAX_AGE_MS = 12 * 60 * 60 * 1000;

/**
 * A draft stamped in the future means the device clock moved (tz/DST/manual
 * change), not that the draft is fresh — beyond a small tolerance, drop it
 * rather than let it live forever.
 */
const FUTURE_SKEW_TOLERANCE_MS = 5 * 60 * 1000;

const draftAddonSchema = z.object({
  productId: z.number().int().positive(),
  quantity: z.number().int().positive(),
});

const draftLineSchema = z.object({
  /** Sellable row id — for a variant this is the VARIANT row, as on the wire. */
  productId: z.number().int().positive(),
  /** Variable parent, kept so a restored variant re-inherits its addons_config. */
  parentId: z.number().int().positive().nullable().default(null),
  quantity: z.number().int().positive(),
  /** Variant/option selections only — see the module header. */
  attributes: z.record(z.string(), z.string()).default({}),
  addons: z.array(draftAddonSchema).default([]),
});

const draftSchema = z.object({
  /** Identity the draft belongs to; a different scope never restores it. */
  scopeKey: z.string().min(1),
  orderType: z.enum(ORDER_TYPE_WIRE_VALUES),
  lines: z.array(draftLineSchema).min(1),
  savedAt: z.number().int().positive(),
  /**
   * The cart's operation id (V2.2-01).
   *
   * This is the ONE thing that makes a retry after a reload safe. If the device
   * died mid-submit, the order may already exist server-side; recovering the
   * ticket with a FRESH key would create a second one, which is exactly the
   * double-charge the key exists to prevent. Carrying it across means the retry
   * resolves to the original order instead.
   *
   * Not PII, and not a credential: it identifies an operation, not a person.
   */
  clientOrderId: z.string().min(1).nullable().default(null),
  /**
   * The draft was last written while a submission was AMBIGUOUS (status 0 —
   * the order may or may not exist server-side). Carried across the reload so
   * the cashier is still told to reconcile against the Queue instead of
   * blindly charging again.
   */
  needsReconciliation: z.boolean().default(false),
});

export type CartDraft = z.infer<typeof draftSchema>;
export type CartDraftLine = z.infer<typeof draftLineSchema>;
export type CartDraftAddon = z.infer<typeof draftAddonSchema>;

/**
 * Defence in depth for the no-free-text rule: `notes` is the key item notes
 * serialize into (attributes.notes), so it can never reach storage even if a
 * caller folds a note into the attribute map.
 */
function sanitizeAttributes(attributes: Record<string, string>): Record<string, string> {
  const clean: Record<string, string> = {};
  for (const [key, value] of Object.entries(attributes)) {
    if (key === 'notes') continue;
    clean[key] = value;
  }
  return clean;
}

/** Persist the draft. An empty ticket clears the key instead of writing one. */
export function saveDraft(draft: CartDraft): void {
  if (draft.lines.length === 0) {
    clearDraft();
    return;
  }
  saveJson(DRAFT_STORAGE_KEY, {
    ...draft,
    lines: draft.lines.map((line) => ({
      ...line,
      attributes: sanitizeAttributes(line.attributes),
    })),
  });
}

/**
 * Read the draft for `scopeKey`. Like AuthStore's persisted snapshot, storage
 * is untrusted input: anything unparseable, version-mismatched or past the
 * staleness cutoff is DROPPED (and the key removed) rather than trusted.
 *
 * A draft belonging to ANOTHER scope is left in place — the next write for the
 * current scope overwrites the single key, and deleting it here would let a
 * momentarily-unresolved scope destroy a good draft.
 */
export function loadDraft(scopeKey: string, nowMs: number = Date.now()): CartDraft | null {
  const raw = loadJson<unknown>(DRAFT_STORAGE_KEY);
  if (raw === null) return null;

  const parsed = draftSchema.safeParse(raw);
  if (!parsed.success) {
    removeKey(DRAFT_STORAGE_KEY);
    return null;
  }

  const draft = parsed.data;
  const age = nowMs - draft.savedAt;
  if (age > DRAFT_MAX_AGE_MS || age < -FUTURE_SKEW_TOLERANCE_MS) {
    removeKey(DRAFT_STORAGE_KEY);
    return null;
  }
  if (draft.scopeKey !== scopeKey) return null;

  return {
    ...draft,
    lines: draft.lines.map((l) => ({ ...l, attributes: sanitizeAttributes(l.attributes) })),
  };
}

export function clearDraft(): void {
  removeKey(DRAFT_STORAGE_KEY);
}
